import { type MouseEvent as ReactMouseEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, CloseIcon, InputTextarea, ResizeCornerIcon } from '@repo/atomic-components';
import { useFocusTrap } from '@/hooks';

export interface FeedbackOption {
  label: string;
  value: string;
}

interface FeedbackModalProps {
  visible: boolean;
  onHide: () => void;
  options: FeedbackOption[];
  onSubmit: (reason: string, comment: string) => Promise<void>;
  isLoading: boolean;
  defaultHeight?: number;
}

export const FeedbackModal = ({
  visible,
  onHide,
  options,
  onSubmit,
  isLoading,
  defaultHeight = 730,
}: FeedbackModalProps) => {
  const DEFAULT_WIDTH = 800;
  const DEFAULT_HEIGHT = defaultHeight;
  const MIN_WIDTH = 560;
  const MIN_HEIGHT = 590;
  const VIEWPORT_MARGIN = 16;

  const [selectedReason, setSelectedReason] = useState('');
  const [comment, setComment] = useState('');

  // Centres the panel for the current viewport, using the size it will actually
  // render at once the viewport clamp in `panelStyle` has been applied.
  const getCenteredPosition = useCallback(() => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const width = Math.min(DEFAULT_WIDTH, viewportWidth - VIEWPORT_MARGIN * 2);
    const height = Math.min(DEFAULT_HEIGHT, viewportHeight - VIEWPORT_MARGIN * 2);

    return {
      x: Math.max(VIEWPORT_MARGIN, Math.floor((viewportWidth - width) / 2)),
      y: Math.max(VIEWPORT_MARGIN, Math.floor((viewportHeight - height) / 2)),
    };
  }, [DEFAULT_HEIGHT, DEFAULT_WIDTH]);

  // Both hold the requested geometry; `panelStyle` clamps it to the viewport.
  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
  const [position, setPosition] = useState(getCenteredPosition);

  const panelRef = useRef<HTMLDivElement>(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const optionsLabelId = useId();
  const commentFieldId = useId();

  const setFocusTrapRef = useFocusTrap<HTMLDivElement>({ active: visible });

  // The panel node is needed both by the focus trap (callback ref) and by drag/resize (`panelRef`).
  const setPanelRef = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node;
      setFocusTrapRef(node);
    },
    [setFocusTrapRef],
  );

  const resizeStartRef = useRef({
    x: 0,
    y: 0,
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
  });

  const isDraggingRef = useRef(false);
  const isResizingRef = useRef(false);

  const isConfirmEnabled = !!(selectedReason && comment.trim().length > 0);

  const constrainPosition = useCallback((x: number, y: number, width: number, height: number) => {
    const maxX = Math.max(0, window.innerWidth - width);
    const maxY = Math.max(0, window.innerHeight - height);

    return {
      x: Math.max(0, Math.min(x, maxX)),
      y: Math.max(0, Math.min(y, maxY)),
    };
  }, []);

  const handleHide = useCallback(() => {
    setSelectedReason('');
    setComment('');

    setSize({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
    setPosition(getCenteredPosition());

    onHide();
  }, [DEFAULT_HEIGHT, DEFAULT_WIDTH, getCenteredPosition, onHide]);

  // Re-centre on open, since the viewport may have changed while hidden.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!visible) {
      return;
    }

    setSize({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
    setPosition(getCenteredPosition());
  }, [DEFAULT_HEIGHT, DEFAULT_WIDTH, getCenteredPosition, visible]);
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    if (!visible) {
      return;
    }

    const onMouseMove = (event: MouseEvent) => {
      // Read the panel back from the DOM: it renders at the viewport-clamped
      // geometry, which is what dragging and resizing have to work against.
      const rect = panelRef.current?.getBoundingClientRect();

      if (!rect) {
        return;
      }

      if (isDraggingRef.current) {
        const nextX = event.clientX - dragOffsetRef.current.x;
        const nextY = event.clientY - dragOffsetRef.current.y;

        setPosition(constrainPosition(nextX, nextY, rect.width, rect.height));
      }

      if (isResizingRef.current) {
        const deltaX = event.clientX - resizeStartRef.current.x;
        const deltaY = event.clientY - resizeStartRef.current.y;

        const maxWidth = window.innerWidth - rect.left;
        const maxHeight = window.innerHeight - rect.top;

        const nextWidth = Math.min(
          maxWidth,
          Math.max(Math.min(MIN_WIDTH, maxWidth), resizeStartRef.current.width + deltaX),
        );

        const nextHeight = Math.min(
          maxHeight,
          Math.max(Math.min(MIN_HEIGHT, maxHeight), resizeStartRef.current.height + deltaY),
        );

        setSize({
          width: nextWidth,
          height: nextHeight,
        });
      }
    };

    const onMouseUp = () => {
      isDraggingRef.current = false;
      isResizingRef.current = false;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [MIN_HEIGHT, MIN_WIDTH, constrainPosition, visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleHide();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleHide, visible]);

  const handleConfirm = async () => {
    await onSubmit(selectedReason, comment.trim());

    handleHide();
  };

  const Footer = (
    <div className="flex justify-end gap-4">
      <Button
        type="button"
        label="Cancel"
        onClick={handleHide}
        outlined
        className="!text-theme-black !border-theme-border-grey !bg-theme-white hover:bg-theme-background-grey! flex !h-8 min-w-17.75 justify-center"
      />

      <Button
        type="button"
        label="Confirm"
        onClick={handleConfirm}
        disabled={!isConfirmEnabled || isLoading}
        loading={isLoading}
        className="bg-theme-dark-green hover:bg-theme-dark-green text-theme-white flex !h-8 !min-w-17.75 items-center justify-center text-sm disabled:opacity-50"
      />
    </div>
  );

  const handleHeaderMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !panelRef.current) {
      return;
    }

    const rect = panelRef.current.getBoundingClientRect();

    dragOffsetRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };

    isDraggingRef.current = true;

    event.preventDefault();
  };

  const handleResizeMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !panelRef.current) {
      return;
    }

    const rect = panelRef.current.getBoundingClientRect();

    resizeStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      width: rect.width,
      height: rect.height,
    };

    isResizingRef.current = true;

    event.preventDefault();
    event.stopPropagation();
  };

  // Clamped in CSS, since `resize` doesn't reliably fire on zoom.
  const panelStyle = useMemo(() => {
    const width = `min(${size.width}px, calc(100vw - ${VIEWPORT_MARGIN * 2}px))`;
    const height = `min(${size.height}px, calc(100vh - ${VIEWPORT_MARGIN * 2}px))`;

    return {
      width,
      height,
      left: `clamp(${VIEWPORT_MARGIN}px, ${position.x}px, calc(100vw - ${width} - ${VIEWPORT_MARGIN}px))`,
      top: `clamp(${VIEWPORT_MARGIN}px, ${position.y}px, calc(100vh - ${height} - ${VIEWPORT_MARGIN}px))`,
    };
  }, [position.x, position.y, size.height, size.width]);

  if (!visible) {
    return null;
  }

  return createPortal(
    <div
      ref={setPanelRef}
      style={panelStyle}
      className="bg-theme-white fixed z-50 flex flex-col overflow-hidden rounded-md shadow-xl outline-none"
      role="dialog"
      aria-modal="true"
      aria-label="Help us improve"
      tabIndex={-1}
    >
      <div
        onMouseDown={handleHeaderMouseDown}
        className="border-theme-border-grey mt-6 flex shrink-0 cursor-move items-center justify-between border-b pb-6"
      >
        <div className="text-theme-black ml-6 flex items-center text-[1.25rem] font-semibold">Help us improve</div>

        <button
          type="button"
          onClick={handleHide}
          aria-label="Close feedback modal"
          className="mr-6 inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded"
        >
          <CloseIcon width={14} height={14} className="text-theme-black" />
        </button>
      </div>

      <div className="flex flex-1 flex-col overflow-auto px-6 pt-8 pb-0">
        <div className="flex flex-1 flex-col gap-6">
          <div className="shrink-0">
            <p id={optionsLabelId} className="text-theme-black mb-6 text-sm leading-6 font-semibold">
              Select an option that best describes your experience
            </p>

            <div
              role="radiogroup"
              aria-labelledby={optionsLabelId}
              className="border-theme-border-grey flex flex-col rounded-[4px] border"
            >
              {options.map((option: FeedbackOption) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selectedReason === option.value}
                  onClick={() => setSelectedReason(option.value)}
                  className="hover:bg-theme-background-grey flex h-9 cursor-pointer items-center gap-3 px-10.5 !py-1.5 text-left transition-colors"
                >
                  <span
                    className={`text-sm font-bold transition-colors ${
                      selectedReason === option.value ? 'text-theme-dark-green' : 'text-theme-black'
                    }`}
                  >
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <label
              htmlFor={commentFieldId}
              className="text-theme-black mb-6 block h-6 shrink-0 text-sm leading-6 font-semibold"
            >
              Please describe your experience in detail.
            </label>

            <InputTextarea
              id={commentFieldId}
              value={comment}
              onChange={({ target }) => setComment(target.value)}
              placeholder="Additional feedback..."
              rows={4}
              className="!text-theme-black !border-theme-border-grey !bg-theme-white !h-full !min-h-[100px] w-full resize-none overflow-y-auto rounded-[8px] border !p-6 !text-sm"
            />
          </div>
        </div>
      </div>

      <div className="shrink-0 px-6 pt-6 pb-4">{Footer}</div>

      <div
        className="absolute right-0 bottom-0 h-6 w-6 cursor-se-resize"
        onMouseDown={handleResizeMouseDown}
        aria-hidden
      >
        <ResizeCornerIcon width={24} height={24} />
      </div>
    </div>,
    document.body,
  );
};
